import React from "react";
import { Link } from "react-router-dom";
import { Sparkles } from "lucide-react";

/**
 * FeedNowPlayingPanel — title + location/date + producer credit +
 * sponsor links block. Rendered as ONE of three stacked sub-blocks
 * inside the unified left-column card (see FeedPlayerOverlay).
 *
 * Iter-57++++ layout change (May 28, 2026): stripped of its outer
 * `bg-gray-900/80 rounded-lg p-3` wrapper because the parent container
 * in FeedPlayerOverlay now owns the rounded chrome. This block is
 * naked-by-design so the Title / Comments / Tip stack reads as a single
 * connected card with internal dividers instead of three floating cards.
 *
 * Also: Nearby Map moved to the right column; Tip CTA extracted to
 * FeedTipBlock. This component is now ONLY the meta block.
 */
const FeedNowPlayingPanel = ({
  activeVideos,
  currentVideoIndex,
  user,
  followingUsers,
  // Handlers
  handleClosePlayer,
  handleFollowFromFeed,
}) => {
  const v = activeVideos[currentVideoIndex];

  return (
    <div className="p-3 flex flex-col" data-testid="feed-now-playing-panel">
      <h3 className="text-white font-bold text-base leading-tight mb-1.5 break-words">
        {v?.title}
        <span className="text-gray-500 font-normal text-[11px] ml-2 tabular-nums">
          {currentVideoIndex + 1}/{activeVideos.length}
        </span>
      </h3>

      <div className="bg-gray-800/50 rounded-lg px-3 py-2 space-y-1.5 text-[11px]">
        {/* Location on line 1, Date · Local time on line 2 — Eric's rule
            (Jun 9, 2026): never let "Jun 9," wrap onto its own line while
            "2026 / 09:24" sits on the next. Forcing the date+time onto a
            dedicated line removes the awkward mid-date break entirely. */}
        {(() => {
          if (!v) return null;
          const city = v.location?.city || "Unknown";
          const country = v.location?.country || "";
          const created = v.created_at ? new Date(v.created_at) : null;
          const dateStr = created
            ? created.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
            : "";
          const timeStr = created
            ? created.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", hour12: false })
            : "";
          return (
            <div className="leading-tight">
              <p className="text-white font-medium" data-testid="feed-meta-location">
                {city}{country ? `, ${country}` : ""}
              </p>
              {(dateStr || timeStr) && (
                <p className="text-gray-300 whitespace-nowrap overflow-hidden text-ellipsis" data-testid="feed-meta-datetime">
                  <span>{dateStr}</span>
                  {dateStr && timeStr && <span className="text-gray-500"> &nbsp;/&nbsp; </span>}
                  <span>{timeStr}</span>
                </p>
              )}
            </div>
          );
        })()}

        {/* PRODUCER / OWNER credit line + Follow pill */}
        {(() => {
          const vv = v || {};
          const isSold = !!vv.sold_at && vv.owner_username && vv.producer_username && vv.owner_username !== vv.producer_username;
          const showOrig = vv.show_original_producer !== false;
          const credit = isSold && !showOrig
            ? { label: 'Owned by', handle: vv.owner_username }
            : { label: 'Produced by', handle: vv.producer_username || vv.username };
          return (
            <div className="flex items-center justify-between gap-2 pt-0.5">
              <p className="text-gray-300 truncate min-w-0">
                <span className="text-gray-500 uppercase tracking-wide text-[10px]">{credit.label}</span>{" "}
                <Link
                  to={`/${credit.handle}`}
                  className="text-emerald-400 hover:underline font-medium"
                  onClick={handleClosePlayer}
                >
                  @{credit.handle}
                </Link>
              </p>
              {user && credit.handle !== user.username && (
                <button
                  onClick={() => handleFollowFromFeed(credit.handle)}
                  className={`text-[10px] px-2 py-0.5 rounded-full transition-all shrink-0 ${
                    followingUsers[credit.handle]
                      ? 'bg-gray-600 text-gray-300 hover:bg-red-500/20 hover:text-red-400'
                      : 'bg-emerald-600 text-white hover:bg-emerald-500'
                  }`}
                >
                  {followingUsers[credit.handle] ? 'Following' : 'Follow'}
                </button>
              )}
            </div>
          );
        })()}

        {/* Co-Produced (only if present) */}
        {v?.co_producer_id && (
          <p className="text-gray-300" data-testid="feed-coproducer-badge">
            <span className="text-amber-300/80 uppercase tracking-wide text-[10px] inline-flex items-center gap-0.5">
              <Sparkles className="h-2.5 w-2.5" /> Co-Prod by
            </span>{" "}
            {v.co_producer_anonymous ? (
              <span className="text-gray-400 italic">anonymous</span>
            ) : v.co_producer_username ? (
              <Link
                to={`/${v.co_producer_username}`}
                className="text-amber-300 hover:underline font-medium"
                onClick={handleClosePlayer}
              >
                @{v.co_producer_username}
              </Link>
            ) : null}
          </p>
        )}

        {/* SPONSORED BY — inline list */}
        {(() => {
          const vv = v || {};
          const producerLinks = vv.producer_affiliate_links || [];
          const coProducerLinks = (!vv.co_producer_anonymous ? (vv.co_producer_affiliate_links || []) : []);
          const merged = [
            ...producerLinks.slice(0, 2).map((l) => ({ link: l, group: 'producer' })),
            ...coProducerLinks.slice(0, 2).map((l) => ({ link: l, group: 'coproducer' })),
          ];
          const defaultLinks = [
            { title: 'ANCESTRY', url: 'https://refer.ancestry.com/ehouser.com' },
            { title: 'INQ', url: 'https://inq.refr.cc/default/u/ehousercom?s=sp&t=cp' }
          ];
          const linksToShow = merged.length > 0 ? merged : defaultLinks.map((l) => ({ link: l, group: 'producer' }));
          return (
            <p className="text-gray-300 truncate">
              <span className="text-gray-500 uppercase tracking-wide text-[10px]">Sponsored by</span>{" "}
              {linksToShow.map((entry, idx) => {
                const link = entry.link;
                const url = typeof link === 'object' ? link.url : link;
                const title = typeof link === 'object' && link.title ? link.title : url.replace(/^https?:\/\//, '').replace(/\/$/, '');
                return (
                  <React.Fragment key={(typeof link === 'object' ? link.url : link) || idx}>
                    <a
                      href={url.startsWith('http') ? url : `https://${url}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`hover:underline font-medium ${entry.group === 'coproducer' ? 'text-amber-300' : 'text-emerald-400'}`}
                      title={entry.group === 'coproducer' ? 'Co-producer link' : 'Producer link'}
                    >
                      {title}
                    </a>
                    {idx < linksToShow.length - 1 && <span className="text-gray-500">, </span>}
                  </React.Fragment>
                );
              })}
            </p>
          );
        })()}
      </div>
    </div>
  );
};

export default FeedNowPlayingPanel;
